import {type MutableRefObject} from 'react'
import {FlatList, Image, View} from 'react-native'
import {Trans} from '@lingui/macro'
import {useQuery} from '@tanstack/react-query'

import {useScrollHandlers} from '#/lib/ScrollContext'
import {getClient} from '#/lib/matrixClient'
import {listPrivateFeed} from '#/lib/private/PrivateAccountService'
import {type PrivatePostEvent} from '#/lib/private/privateTypes'
import {useProfileQuery} from '#/state/queries/profile'
import {useAgent} from '#/state/session'
import {PreviewableUserAvatar} from '#/view/com/util/UserAvatar'
import {atoms as a, useTheme} from '#/alf'
import {Text} from '#/components/Typography'

function formatTimestamp(ts?: string) {
  if (!ts) return ''
  const date = new Date(ts)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString()
}

function renderMedia(items: PrivatePostEvent['media'] | undefined) {
  if (!items || items.length === 0) return null
  const client = getClient()
  return (
    <View style={[a.flex_col, a.gap_sm]}>
      {items.map((item, index) => {
        const uri = client?.mxcUrlToHttp?.(item.mxc, 1024, 1024, 'scale')
        if (!uri) return null
        return (
          <Image
            key={`${item.mxc}-${index}`}
            source={{uri}}
            resizeMode="cover"
            style={[
              a.w_full,
              a.rounded_md,
              {
                aspectRatio:
                  item.width && item.height ? item.width / item.height : 1.5,
              },
            ]}
          />
        )
      })}
    </View>
  )
}

export function PrivateFeed({
  did,
  headerHeight = 0,
  scrollElRef,
}: {
  did: string
  headerHeight?: number
  scrollElRef?: MutableRefObject<any>
}) {
  const t = useTheme()
  const agent = useAgent()
  const scrollHandlers = useScrollHandlers()
  const {data: profile} = useProfileQuery({did})

  const {data, isLoading} = useQuery({
    queryKey: ['private-feed', did],
    queryFn: () => listPrivateFeed(did, agent),
    enabled: !!did,
    staleTime: 30 * 1000,
  })

  if (isLoading) {
    return (
      <View style={[a.px_lg, a.py_lg]}>
        <Text style={[t.atoms.text_contrast_medium]}>
          <Trans>Loading private posts...</Trans>
        </Text>
      </View>
    )
  }

  if (!data || data.length === 0) {
    return (
      <View style={[a.px_lg, a.py_lg]}>
        <Text style={[t.atoms.text_contrast_medium]}>
          <Trans>No private posts yet.</Trans>
        </Text>
      </View>
    )
  }

  return (
    <FlatList
      data={data}
      keyExtractor={item => item.id}
      ref={scrollElRef as any}
      onScroll={scrollHandlers.onScroll}
      onScrollBeginDrag={scrollHandlers.onBeginDrag}
      onScrollEndDrag={scrollHandlers.onEndDrag}
      onMomentumScrollEnd={scrollHandlers.onMomentumEnd}
      scrollEventThrottle={16}
      contentContainerStyle={[
        a.px_lg,
        a.pb_4xl,
        a.gap_lg,
        {paddingTop: headerHeight},
      ]}
      renderItem={({item}) => {
        const fallbackProfile = profile || ({
          did,
          handle: did,
          displayName: did,
        } as any)
        return (
        <View style={[a.py_md, a.border_t, t.atoms.border_contrast_medium]}>
          <View style={[a.flex_row, a.align_center, a.gap_sm, a.pb_sm]}>
            <PreviewableUserAvatar
              size={40}
              profile={fallbackProfile}
              moderation={undefined}
              type={profile?.associated?.labeler ? 'labeler' : 'user'}
            />
            <View style={[a.flex_1]}>
              <Text style={[a.font_bold, t.atoms.text]}>
                {profile?.displayName || profile?.handle || did}
              </Text>
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                {formatTimestamp(item.createdAt)}
              </Text>
            </View>
          </View>
          {!!item.text && (
            <Text style={[a.text_md, t.atoms.text, a.pb_sm]}>
              {item.text}
            </Text>
          )}
          {renderMedia(item.media)}
        </View>
        )
      }}
    />
  )
}
